import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "背景透過したのにAmazonで白背景にならない PNG→JPEG変換の落とし穴",
	description:
		"背景除去後のPNGをJPEGに変換するとAmazonで白背景にならない原因（アルファチャンネル）と、正しく白背景JPEGを作る手順を解説。",
	publishDate: "2026-03-16",
	slug: "transparent-png-to-white-jpeg-amazon",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function TransparentPngToWhiteJpegAmazon() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							背景除去ツールで商品を切り抜き、PNGをJPEGに変換してAmazonにアップロードしたのに「背景が白になっていない」と弾かれる——この問題、ツールの使い方ではなく「JPEG形式の仕様」が原因です。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							原因：JPEGはアルファチャンネル（透明情報）に対応していない
						</h2>
						<p className="text-gray-700 mb-4">
							PNG形式には「アルファチャンネル」と呼ばれる透明度の情報が含まれています。背景除去ツールが出力するPNG画像の背景は「白」ではなく「透明（アルファ値ゼロ）」な状態です。
						</p>
						<p className="text-gray-700 mb-4">
							ここで問題になるのが、
							<strong>JPEGはアルファチャンネルを持てない</strong>
							という仕様です。PNG→JPEG変換を行うと、透明部分をなんらかの色に置き換えなければなりません。
							変換ツールのデフォルト設定によってこの色が異なります：
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>
								<span className="font-semibold">多くのツール：</span>
								透明部分が黒に置き換わる（黒背景JPEGになる）
							</li>
							<li>
								<span className="font-semibold">一部のビューアー：</span>
								グレーやチェック柄で見えるが、データは黒や不定値
							</li>
							<li>
								<span className="font-semibold">Windowsフォトビューアー：</span>
								白く見えるが実際は黒のことも（表示と実データが異なる）
							</li>
						</ul>
						<p className="text-gray-700">
							Amazonはアップロードされた画像の実際のピクセルデータを分析するため、「見た目が白に見える」だけでは通過しません。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							正しく白背景JPEGを作る手順
						</h2>

						<h3 className="text-lg font-medium mt-6 mb-3">
							手順1: 透明PNG→白塗りつぶし→JPEG保存（GIMP使用）
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>GIMPで透明PNGを開く</li>
							<li>
								「画像」メニュー
								→「統合画像」をクリック（背景色が白であることを確認）
							</li>
							<li>透明部分が白で塗りつぶされる</li>
							<li>
								「ファイル」→「上書きエクスポート」または「エクスポート」でJPEG保存
							</li>
						</ol>

						<h3 className="text-lg font-medium mt-6 mb-3">
							手順2: Irfanviewで白背景指定してJPEG変換
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>Irfanviewを起動し、透明PNGを開く</li>
							<li>「オプション」→「プロパティ/設定」→「PNG→他の形式」</li>
							<li>「透明部分の背景色」を白（#FFFFFF）に設定</li>
							<li>「ファイル」→「名前を付けて保存」でJPEG形式を選択</li>
						</ol>

						<h3 className="text-lg font-medium mt-6 mb-3">
							手順3: ブラウザだけで完結（一番簡単）
						</h3>
						<p className="text-gray-700 mb-2">
							しろぬきやを使えば、上記の複雑な手順は一切不要です。
							商品画像をアップロードするだけで、背景除去→白背景塗りつぶし→Amazon規格（1000×1000px以上）へのリサイズまで自動で完了します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							確認方法：本当に白背景になっているか？
						</h2>
						<p className="text-gray-700 mb-4">
							作成したJPEGが本当に白背景かどうかは、以下の方法で確認できます：
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700">
							<li>
								GIMPやPhotoshopで開き、背景部分のピクセルをスポイトで確認→ R:255
								G:255 B:255 なら白
							</li>
							<li>
								Windowsのペイントで開いて白塗り部分を別の色で塗ってみる→
								変化がなければ白
							</li>
							<li>しろぬきやで出力した画像はすべて白背景保証（確認不要）</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							PNG→JPEG変換の落とし穴を気にせず、最初から「白背景のAmazon規格サイズJPEG」を一発で作れるのがしろぬきやの強みです。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
